/**
 * GroupActionsMenu — reusable ⋮ (three-dot) broadcast-group management menu.
 *
 * Renders the button + dropdown with 6 actions and owns the state for the 4
 * read-only modals (View Members, Delete confirmation, Shared Media, Broadcast
 * History). Two actions delegate to the caller because they open editors that
 * live on the WhatsApp page:
 *   - Edit Group  → onEdit(group)
 *   - Add Member  → onAddMember(group)   (default: navigate to Customers?import=1)
 *
 * Usage:
 *   <GroupActionsMenu group={g} onEdit={openEditModal} onChanged={reload} />
 *
 * The parent renders this INSIDE a group card. Click on the ⋮ button never
 * bubbles up to the card's own click handler (e.g. broadcast group-selection
 * toggle), so callers can drop it into any existing card safely.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  MoreVertical, Users, UserPlus, Pencil, Trash2, X, Loader2, Search,
  Image as ImageIcon, History as HistoryIcon, Calendar, ShoppingCart, ExternalLink,
} from 'lucide-react';
import api, { fileUrl } from '../../lib/api';
import StatusBadge from '../shared/StatusBadge';

// Approximate rendered size of the dropdown — used to decide up/down + left/right
// placement so the menu is never clipped by the viewport edges. These values just
// need to be within a few px of the real rendered box; the browser will do final
// pixel layout regardless.
const MENU_W = 220;
const MENU_H = 296;   // 6 items × ~44px + divider + padding
const GAP = 6;        // gap between the ⋮ button and the menu

export default function GroupActionsMenu({ group, onEdit, onAddMember, onChanged }) {
  const nav = useNavigate();
  const btnRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);  // {top, left} in viewport px when open
  const [viewMembers, setViewMembers] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [sharedMedia, setSharedMedia] = useState(false);
  const [broadcastHistory, setBroadcastHistory] = useState(false);

  const gname = group.group_name || group.name || 'Untitled group';

  // Compute the ideal viewport position for the dropdown given the ⋮ button's
  // current bounding rect. Flips above when there's not enough space below,
  // and clamps horizontally so the menu stays fully inside the viewport.
  const computePosition = useCallback(() => {
    const btn = btnRef.current;
    if (!btn) return null;
    const r = btn.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // Vertical — prefer below the button; flip above if below would overflow.
    const spaceBelow = vh - r.bottom;
    const spaceAbove = r.top;
    const openAbove = spaceBelow < MENU_H + GAP && spaceAbove > spaceBelow;
    const top = openAbove ? Math.max(8, r.top - MENU_H - GAP) : Math.min(vh - MENU_H - 8, r.bottom + GAP);

    // Horizontal — align the menu's right edge with the button's right edge
    // (standard "under-the-kebab" alignment), then clamp inside the viewport.
    let left = r.right - MENU_W;
    if (left + MENU_W > vw - 8) left = vw - MENU_W - 8;
    if (left < 8) left = 8;

    return { top, left };
  }, []);

  // Open handler — compute position first so the portal renders in the right
  // place on first paint (no flicker).
  const openMenu = useCallback(() => {
    const p = computePosition();
    if (p) setPos(p);
    setOpen(true);
  }, [computePosition]);

  // Recompute position while open on scroll/resize, and close cleanly if the
  // button leaves the viewport (e.g. seller scrolls the group list past it).
  useEffect(() => {
    if (!open) return;
    const onScrollOrResize = () => {
      const btn = btnRef.current;
      if (!btn) { setOpen(false); return; }
      const r = btn.getBoundingClientRect();
      if (r.bottom < 0 || r.top > window.innerHeight || r.right < 0 || r.left > window.innerWidth) {
        setOpen(false);
        return;
      }
      const p = computePosition();
      if (p) setPos(p);
    };
    // capture:true so we hear scroll events from every scrolling ancestor.
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open, computePosition]);

  // Escape key closes the menu.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const doDelete = async () => {
    try {
      await api.delete(`/whatsapp/groups/${group.id}`);
      toast.success('Group deleted');
      setConfirmDelete(false);
      onChanged && onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const handleAddMember = (e) => {
    e.stopPropagation();
    setOpen(false);
    if (onAddMember) return onAddMember(group);
    nav(`/dashboard/customers?import=1&group_id=${encodeURIComponent(group.id)}&group_name=${encodeURIComponent(gname)}`);
  };

  const handleEdit = (e) => {
    e.stopPropagation();
    setOpen(false);
    if (onEdit) return onEdit(group);
    // Fallback for callers (like Broadcast page) that don't own the edit modal —
    // route to the WhatsApp Groups tab with an edit intent.
    nav(`/dashboard/whatsapp?tab=groups&edit_group=${encodeURIComponent(group.id)}`);
  };

  return (
    <div className="relative inline-block" onClick={(e) => e.stopPropagation()}>
      <button
        ref={btnRef}
        data-testid={`wa-group-menu-btn-${group.id}`}
        aria-label="Group actions"
        onClick={(e) => { e.stopPropagation(); e.preventDefault(); open ? setOpen(false) : openMenu(); }}
        className="p-1 rounded-lg hover:bg-fab-elevated text-fab-text-secondary hover:text-fab-text"
      >
        <MoreVertical className="w-4 h-4" />
      </button>
      {open && pos && createPortal(
        <>
          {/* Click-outside catcher — sits above every scrolling container so
              clicks on the group card can never sneak through and toggle
              selection while the menu is open. */}
          <div
            className="fixed inset-0 z-[999]"
            onClick={(e) => { e.stopPropagation(); setOpen(false); }}
          />
          <div
            data-testid={`wa-group-menu-${group.id}`}
            role="menu"
            style={{ position: 'fixed', top: pos.top, left: pos.left, width: MENU_W, zIndex: 1000 }}
            className="rounded-xl bg-white shadow-lg border border-fab-primary-lighter overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <MenuItem icon={<Users className="w-4 h-4" />} label="View Members"
              testid={`wa-menu-view-members-${group.id}`}
              onClick={(e) => { e.stopPropagation(); setOpen(false); setViewMembers(true); }} />
            <MenuItem icon={<UserPlus className="w-4 h-4" />} label="Add Member"
              testid={`wa-menu-add-member-${group.id}`} onClick={handleAddMember} />
            <MenuItem icon={<Pencil className="w-4 h-4" />} label="Edit Group"
              testid={`wa-menu-edit-${group.id}`} onClick={handleEdit} />
            <MenuItem icon={<ImageIcon className="w-4 h-4" />} label="Shared Media"
              testid={`wa-menu-media-${group.id}`}
              onClick={(e) => { e.stopPropagation(); setOpen(false); setSharedMedia(true); }} />
            <MenuItem icon={<HistoryIcon className="w-4 h-4" />} label="Broadcast History"
              testid={`wa-menu-history-${group.id}`}
              onClick={(e) => { e.stopPropagation(); setOpen(false); setBroadcastHistory(true); }} />
            <div className="border-t border-fab-primary-lighter" />
            <MenuItem icon={<Trash2 className="w-4 h-4" />} label="Delete Group" danger
              testid={`wa-menu-delete-${group.id}`}
              onClick={(e) => { e.stopPropagation(); setOpen(false); setConfirmDelete(true); }} />
          </div>
        </>,
        document.body
      )}
      {viewMembers && (
        <ViewMembersModal
          group={group}
          onClose={() => setViewMembers(false)}
          onChanged={onChanged}
          onNavigateCustomer={(phone) => nav(`/dashboard/customers?search=${encodeURIComponent(phone)}`)}
        />
      )}
      {confirmDelete && (
        <DeleteGroupModal
          group={group}
          onCancel={() => setConfirmDelete(false)}
          onConfirm={doDelete}
        />
      )}
      {sharedMedia && <SharedMediaModal group={group} onClose={() => setSharedMedia(false)} />}
      {broadcastHistory && <BroadcastHistoryModal group={group} onClose={() => setBroadcastHistory(false)} />}
    </div>
  );
}

function MenuItem({ icon, label, onClick, danger, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`w-full flex items-center gap-2.5 px-3.5 py-2.5 text-[13px] text-left transition-colors ${danger ? 'text-red-700 hover:bg-red-50' : 'text-fab-text hover:bg-fab-elevated'}`}
    >
      {icon}<span className="font-semibold">{label}</span>
    </button>
  );
}

function StatCell({ label, value, hint }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-fab-text-muted font-semibold">{label}</div>
      <div className="text-[13px] font-bold text-fab-text mt-0.5">{value}</div>
      {hint && <div className="text-[10px] text-fab-text-muted italic">{hint}</div>}
    </div>
  );
}

// ── View Members modal ─────────────────────────────────────────────────────
function ViewMembersModal({ group, onClose, onChanged, onNavigateCustomer }) {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [removing, setRemoving] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: md }, { data: st }] = await Promise.all([
        api.get(`/whatsapp/groups/${group.id}/members-detailed`),
        api.get(`/whatsapp/groups/${group.id}/stats`),
      ]);
      setRows(md.members || []);
      setStats(st);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load members');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [group.id]);

  const filtered = rows.filter((r) => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return (r.name || '').toLowerCase().includes(s)
      || (r.phone || '').toLowerCase().includes(s)
      || (r.business_name || '').toLowerCase().includes(s);
  });

  const removeMember = async (phone) => {
    setRemoving(phone);
    try {
      await api.delete(`/whatsapp/groups/${group.id}/members/${encodeURIComponent(phone)}`);
      toast.success('Removed from group');
      await load();
      onChanged && onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Remove failed');
    } finally {
      setRemoving('');
    }
  };

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—';
  const gname = group.group_name || group.name || 'Untitled group';

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="wa-view-members-modal">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[calc(100vh_-_2rem)] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-fab-primary-lighter flex items-center justify-between">
          <div>
            <div className="font-display font-bold text-fab-text text-[16px]">{gname}</div>
            <div className="text-[12px] text-fab-text-muted">{rows.length} member{rows.length === 1 ? '' : 's'} in this broadcast group</div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-fab-elevated rounded-lg" data-testid="wa-view-members-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-5 pt-3 pb-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-fab-text-muted" />
            <input
              data-testid="wa-members-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, phone, or business…"
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-fab-primary-lighter text-[13px] focus:outline-none focus:border-fab-primary"
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto divide-y divide-fab-primary-lighter">
          {loading && <div className="p-8 text-center text-fab-text-muted"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>}
          {!loading && filtered.length === 0 && (
            <div className="p-8 text-center text-fab-text-muted text-[13px]">
              {rows.length === 0 ? 'No members yet. Use "Add Member" to import buyers into this group.' : 'No members match your search.'}
            </div>
          )}
          {!loading && filtered.map((m) => (
            <div key={m.phone} className="px-5 py-3 flex items-center gap-3 hover:bg-fab-elevated" data-testid={`wa-member-row-${m.phone}`}>
              <div className="w-9 h-9 rounded-full bg-fab-primary-lighter text-fab-primary flex items-center justify-center text-[13px] font-bold shrink-0">
                {(m.name || m.phone || '?').charAt(m.name ? 0 : Math.max(0, (m.phone || '').length - 2)).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[13px] font-semibold text-fab-text truncate">{m.name || <span className="text-fab-text-muted italic font-normal">(no name)</span>}</div>
                <div className="text-[12px] text-fab-text-muted font-mono">{m.phone}</div>
                {m.business_name && <div className="text-[11px] text-fab-text-secondary truncate">{m.business_name}{m.city ? ` · ${m.city}` : ''}</div>}
              </div>
              <div className="flex items-center gap-1">
                {m.customer_id && (
                  <button
                    data-testid={`wa-member-edit-${m.phone}`}
                    title="Open customer profile"
                    onClick={() => onNavigateCustomer(m.phone)}
                    className="p-1.5 rounded-lg hover:bg-white text-fab-text-secondary hover:text-fab-primary"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                )}
                <button
                  data-testid={`wa-member-remove-${m.phone}`}
                  title="Remove from group"
                  disabled={removing === m.phone}
                  onClick={() => removeMember(m.phone)}
                  className="p-1.5 rounded-lg hover:bg-red-50 text-fab-text-secondary hover:text-red-600 disabled:opacity-50"
                >
                  {removing === m.phone ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                </button>
              </div>
            </div>
          ))}
        </div>
        <div className="border-t border-fab-primary-lighter bg-fab-elevated/50 px-5 py-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-[11px]" data-testid="wa-view-members-stats">
          <StatCell label="Total Members" value={stats?.total_members ?? rows.length} />
          <StatCell label="Last Broadcast" value={stats ? fmtDate(stats.last_broadcast_at) : '—'} />
          <StatCell label="Total Broadcasts" value={stats?.total_broadcasts ?? 0} />
          <StatCell label="Orders Received" value={stats?.orders_count ?? 0} hint="Coming soon" />
        </div>
      </div>
    </div>
  );
}

// ── Delete confirmation ────────────────────────────────────────────────────
function DeleteGroupModal({ group, onCancel, onConfirm }) {
  const gname = group.group_name || group.name || 'this group';
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onCancel} data-testid="wa-delete-group-modal">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="p-5">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-red-100 text-red-700 flex items-center justify-center shrink-0"><Trash2 className="w-5 h-5" /></div>
            <div>
              <div className="font-display font-bold text-fab-text text-[16px]">Delete &quot;{gname}&quot;?</div>
              <div className="text-[13px] text-fab-text-secondary mt-2">
                This will delete the broadcast group only. Customers will remain in your customer database.
              </div>
            </div>
          </div>
        </div>
        <div className="px-5 pb-5 flex items-center justify-end gap-2">
          <button onClick={onCancel} data-testid="wa-delete-cancel" className="px-4 py-2 rounded-lg text-[13px] font-semibold text-fab-text-secondary hover:bg-fab-elevated">Cancel</button>
          <button onClick={onConfirm} data-testid="wa-delete-confirm" className="px-4 py-2 rounded-lg text-[13px] font-semibold bg-red-600 hover:bg-red-700 text-white">Delete</button>
        </div>
      </div>
    </div>
  );
}

// ── Shared Media (WhatsApp-style dedup gallery) ────────────────────────────
function SharedMediaModal({ group, onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const gname = group.group_name || group.name || 'Group';

  useEffect(() => {
    setLoading(true);
    api.get(`/whatsapp/groups/${group.id}/shared-media`)
      .then(({ data }) => setItems(data.items || []))
      .catch((err) => toast.error(err.response?.data?.detail || 'Failed to load media'))
      .finally(() => setLoading(false));
  }, [group.id]);

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-IN', { dateStyle: 'medium' }) : '—';

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="wa-shared-media-modal">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[calc(100vh_-_2rem)] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-fab-primary-lighter flex items-center justify-between">
          <div>
            <div className="font-display font-bold text-fab-text text-[16px]">Shared Media · {gname}</div>
            <div className="text-[12px] text-fab-text-muted">{items.length} unique product{items.length === 1 ? '' : 's'} broadcast to this group</div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-fab-elevated rounded-lg" data-testid="wa-shared-media-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          {loading && <div className="p-8 text-center text-fab-text-muted"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>}
          {!loading && items.length === 0 && (
            <div className="p-10 text-center text-fab-text-muted text-[13px]">
              <ImageIcon className="w-10 h-10 mx-auto mb-2 opacity-40" />
              No media shared with this group yet. Broadcasts sent to this group will appear here.
            </div>
          )}
          {!loading && items.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="wa-shared-media-grid">
              {items.map((p) => (
                <div key={p.id} className="fab-card p-2 hover:shadow-md transition-shadow" data-testid={`wa-media-card-${p.id}`}>
                  <div className="relative aspect-square bg-fab-elevated rounded-lg overflow-hidden">
                    {p.thumb_path ? (
                      <img src={fileUrl(p.thumb_path)} alt={p.name} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-fab-text-muted"><ImageIcon className="w-8 h-8" /></div>
                    )}
                    <span className="absolute top-1.5 right-1.5 px-1.5 py-0.5 rounded-full bg-black/60 text-white text-[10px] font-bold">×{p.sent_times}</span>
                  </div>
                  <div className="mt-2 px-1">
                    <div className="text-[12px] font-semibold text-fab-text truncate" title={p.name}>{p.name}</div>
                    <div className="text-[10px] text-fab-text-muted font-mono">{p.product_code}</div>
                    <div className="text-[10px] text-fab-text-muted mt-0.5 flex items-center gap-1"><Calendar className="w-2.5 h-2.5" /> Last sent {fmtDate(p.last_sent_at)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Broadcast History ──────────────────────────────────────────────────────
function BroadcastHistoryModal({ group, onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();
  const gname = group.group_name || group.name || 'Group';

  useEffect(() => {
    setLoading(true);
    api.get(`/whatsapp/groups/${group.id}/broadcasts`)
      .then(({ data }) => setItems(data.items || []))
      .catch((err) => toast.error(err.response?.data?.detail || 'Failed to load history'))
      .finally(() => setLoading(false));
  }, [group.id]);

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="wa-broadcast-history-modal">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[calc(100vh_-_2rem)] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-fab-primary-lighter flex items-center justify-between">
          <div>
            <div className="font-display font-bold text-fab-text text-[16px]">Broadcast History · {gname}</div>
            <div className="text-[12px] text-fab-text-muted">{items.length} broadcast{items.length === 1 ? '' : 's'} sent to this group</div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-fab-elevated rounded-lg" data-testid="wa-broadcast-history-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading && <div className="p-8 text-center text-fab-text-muted"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>}
          {!loading && items.length === 0 && (
            <div className="p-10 text-center text-fab-text-muted text-[13px]">
              <HistoryIcon className="w-10 h-10 mx-auto mb-2 opacity-40" />
              No broadcasts sent to this group yet.
            </div>
          )}
          {!loading && items.map((b) => (
            <div key={b.id} className="border-b border-fab-primary-lighter last:border-0 p-4 hover:bg-fab-elevated" data-testid={`wa-history-row-${b.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="text-[13px] font-semibold text-fab-text">{fmtDate(b.created_at)}</div>
                    <StatusBadge status={b.status} />
                  </div>
                  <div className="text-[12px] text-fab-text-muted mt-1 flex items-center gap-3 flex-wrap">
                    <span><ShoppingCart className="w-3 h-3 inline mr-0.5" /> {b.total_products} product{b.total_products === 1 ? '' : 's'}</span>
                    <span><Users className="w-3 h-3 inline mr-0.5" /> {b.recipients_count} recipient{b.recipients_count === 1 ? '' : 's'}</span>
                    {b.delivery && b.delivery.total_attempts > 0 && (
                      <>
                        <span className="text-emerald-700">{b.delivery.delivered} delivered</span>
                        {b.delivery.read > 0 && <span className="text-fab-primary">{b.delivery.read} read</span>}
                        {b.delivery.failed > 0 && <span className="text-red-600">{b.delivery.failed} failed</span>}
                      </>
                    )}
                  </div>
                  {(b.products || []).length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {b.products.slice(0, 5).map((p) => (
                        <div key={p.id} className="flex items-center gap-1.5 px-2 py-1 rounded bg-fab-elevated border border-fab-primary-lighter">
                          {p.thumb_path && <img src={fileUrl(p.thumb_path)} alt="" className="w-5 h-5 rounded object-cover" />}
                          <span className="text-[11px] font-mono text-fab-text-secondary">{p.product_code}</span>
                        </div>
                      ))}
                      {b.products.length > 5 && <span className="text-[11px] text-fab-text-muted self-center">+{b.products.length - 5} more</span>}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => { onClose(); nav('/dashboard/broadcast'); }}
                  data-testid={`wa-history-open-${b.id}`}
                  className="shrink-0 text-[11px] font-semibold px-2.5 py-1 rounded border border-fab-primary-lighter text-fab-primary hover:bg-fab-primary-lighter inline-flex items-center gap-1"
                >
                  Open <ExternalLink className="w-3 h-3" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
